"use client";

import React, { CSSProperties } from "react";
import TooltipComponent from "@/components/Tooltip";
import getStandardNumber from "@/common/function/getStandardNumber";
import lengthFromDotToFirstNonZero from "@/common/function/lengthFromDotToFirstNonZero";
import formatter from "@/common/function/formatter";
import styles from "./index.module.css";
import { GeneralPropsType } from "@/common/types";
import clsx from "clsx";
import roundUp from "@/utils/roundUp";
import { useRealtimeData } from "@/contexts/RealtimeData";
import { useGlobalData } from "@/contexts/GlobalData";
interface FormatterNumberProps extends GeneralPropsType {
  value?: number | string;
  start?: string;
  unit?: string;
  unitCustomStyle?: string;
  lengthValueAllowed?: number;
  noUnitSpacing?: boolean;
  allowShowZero?: boolean;
  useCompact?: boolean;
  disableTooltip?: boolean;
  isUnitStyle?: boolean;
  disableAdjustUnitColor?: boolean;
  isLessThan?: boolean;
  overrideLessThanClass?: string;
  unitStyle?: CSSProperties;
  useCompactOnThousand?: boolean;
  startStyle?: CSSProperties;
  hideStart?: boolean;
  hideUnit?: boolean;
  onClickUnit?: (data?: any) => any;
}
export enum UnitDirection {
  LEFT = "left",
  RIGHT = "right",
}
const FormatterNumber: React.FC<FormatterNumberProps> = ({
  value,
  start = "",
  unit = "",
  lengthValueAllowed = 2,
  noUnitSpacing = false,
  allowShowZero = false,
  useCompact = true,
  disableTooltip,
  isUnitStyle,
  unitCustomStyle,
  disableAdjustUnitColor,
  isLessThan,
  overrideLessThanClass,
  unitStyle,
  className,
  useCompactOnThousand = false,
  startStyle,
  hideStart,
  hideUnit,
  onClickUnit,
  ...rest
}) => {
  const {
    tokensRateState
  } = useRealtimeData();
  const {
    userInfo,
    translate
  } = useGlobalData();
  if (value === undefined) return;
  const isFiat = start?.includes("$") || start?.toLowerCase()?.includes("usd");
  const rate = isFiat ? +(tokensRateState?.[userInfo?.settings?.currency || "USD"]?.USD || 1) : 1;
  const numberDisplay = Number(value) / rate;
  const userCurrency = userInfo?.settings?.currency || "USD";
  const ifLeftUnit = translate(`${userCurrency || "USD"}_direction`) === UnitDirection.LEFT;
  const startDisplay = isFiat ? ifLeftUnit ? translate(`${userCurrency || "USD"}_unit`) : "" : start;
  const unitDisplay = isFiat ? ifLeftUnit ? "" : translate(`${userCurrency || "USD"}_unit`) : unit;
  const formattedNumber = getStandardNumber(numberDisplay);
  const lengthFromDot = lengthFromDotToFirstNonZero(formattedNumber);
  const lessThan = numberDisplay > 0 && numberDisplay < 0.01 && isLessThan;
  return <TooltipComponent disableTooltip={disableTooltip} childrenTooltip={<FormatterNumber disableTooltip start={start} value={value} lengthValueAllowed={12} useCompact={false} unit={unit} allowShowZero disableAdjustUnitColor hideStart={hideStart} hideUnit={hideUnit} />} placement="top" data-sentry-element="TooltipComponent" data-sentry-component="FormatterNumber" data-sentry-source-file="index.tsx">
            <div className={clsx("!select-none !whitespace-nowrap", styles.formatted_number, className)}>
                {numberDisplay === 0 && !allowShowZero ? "--" : <>
                        {lessThan && <span className={clsx("relative -top-0.5 font-primary mr-0.5", overrideLessThanClass)}>
                                {"< "}
                            </span>}
                        {!(numberDisplay === 0) && !hideStart && <span className="font-primary" style={startStyle}>
                                {startDisplay}
                            </span>}
                        {!lessThan && numberDisplay > 0 && numberDisplay < 0.000001 ? <span className="font-primary">
                                {"0.0"}
                                <sub>{lengthFromDot}</sub>
                                {formattedNumber.slice(2 + lengthFromDot, lengthValueAllowed - 2 + lengthFromDot)}
                            </span> : <span className="font-primary">
                                {lessThan ? "0.01" : formatter(isLessThan ? Number(numberDisplay).toFixed(2) : numberDisplay, false, useCompact, useCompactOnThousand, lengthValueAllowed)}
                            </span>}
                        {!hideUnit && !!unitDisplay && !noUnitSpacing && !(numberDisplay === 0) && <span className={clsx("font-primary", unitCustomStyle, {
          "text-secondary": isUnitStyle,
          not_apply_adjust_color_tbk: disableAdjustUnitColor
        })} style={unitStyle} onClick={onClickUnit}>
                                {unitDisplay}
                            </span>}
                    </>}
            </div>
        </TooltipComponent>;
};
export default FormatterNumber;